import React from 'react';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Globe, Lock, Ghost, Shield } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { useAgeRestrictions } from '@/hooks/useAgeRestrictions';

interface PrivacyModeSelectorProps {
  value: 'public' | 'private' | 'ghost';
  onChange: (value: 'public' | 'private' | 'ghost') => void;
  disabled?: boolean;
}

export function PrivacyModeSelector({ value, onChange, disabled = false }: PrivacyModeSelectorProps) {
  const { isMinor, canMakePublic, loading: ageLoading } = useAgeRestrictions();
  
  const modes = [
    {
      id: 'public',
      icon: Globe,
      title: 'Public',
      description: 'Anyone can view your profile, reviews, and reading activity',
      features: [
        'Profile discoverable in search',
        'Anyone can follow you',
        'Reviews visible on book pages',
        'Appears in leaderboards'
      ],
      restrictedForMinors: true
    },
    {
      id: 'private',
      icon: Lock,
      title: 'Private',
      description: 'Only approved followers can see your content',
      features: [
        'Profile discoverable in search',
        'Following requires approval',
        'Reviews hidden from non-followers',
        'Limited leaderboard visibility'
      ],
      restrictedForMinors: false
    },
    {
      id: 'ghost',
      icon: Ghost,
      title: 'Ghost Mode',
      description: 'Completely private - only you can see your content',
      features: [
        'Profile hidden from search',
        'No one can follow you',
        'All content private',
        'No public visibility'
      ],
      restrictedForMinors: false
    }
  ];

  const isModeDisabled = (modeId: string) => {
    if (disabled) return true;
    if (isMinor && modeId === 'public' && !canMakePublic) return true;
    return false;
  };

  return (
    <div className="space-y-4">
      {isMinor && !canMakePublic && (
        <Alert className="bg-secondary/10 border-secondary">
          <Shield className="h-4 w-4" />
          <AlertDescription className="text-sm">
            <span className="font-medium">🦖 Age-restricted profile</span>
            <br />
            Your account is private by default for your safety. You'll be able to make it public when you turn 16.
          </AlertDescription>
        </Alert>
      )}

      <RadioGroup value={value} onValueChange={onChange} disabled={disabled || ageLoading}>
        {modes.map((mode) => {
          const modeDisabled = isModeDisabled(mode.id);
          
          return (
            <Card
              key={mode.id}
              className={`p-4 transition-all ${
                value === mode.id
                  ? 'border-primary bg-primary/5'
                  : modeDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:border-muted-foreground/50'
              }`}
              onClick={() => !disabled && !modeDisabled && onChange(mode.id as 'public' | 'private' | 'ghost')}
            >
              <div className="flex items-start gap-4">
                <RadioGroupItem 
                  value={mode.id} 
                  id={mode.id} 
                  disabled={modeDisabled}
                />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <mode.icon className="w-5 h-5" />
                    <Label 
                      htmlFor={mode.id} 
                      className={`text-base font-semibold ${modeDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {mode.title}
                    </Label>
                    {modeDisabled && mode.restrictedForMinors && (
                      <span className="text-xs text-muted-foreground">(Available at 16)</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{mode.description}</p>
                  <ul className="text-xs text-muted-foreground space-y-1 ml-1">
                    {mode.features.map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          );
        })}
      </RadioGroup>

      <Alert>
        <AlertDescription className="text-sm">
          Your email address is always private and never shown publicly, regardless of your privacy mode.
        </AlertDescription>
      </Alert>
    </div>
  );
}
